import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import style from "./bmp-setup.scss";
import { Licenses } from "components/icons/Licenses";
import { OnPremBmp } from "components/icons/OnPremBmp";
import { Reports } from "components/icons/Reports";
import { Users } from "components/icons/Users";
import { Workflows } from "components/icons/Workflows";
import Modal from "components/modal/Modal";
import SurveyMonkeyWidget from "components/products/SurveyMonkeyWidget";
import { ALL_MCS_ROUTE, ALL_REPORTS_ROUTE, ALL_WORKFLOWS_ROUTE, USERS_ALL_USERS_ROUTE } from "components/router/Routes";
import {
    AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN,
    AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED,
    AUTH_EMS_CREATE_ENTITLEMENT,
    AUTH_MC_VIEW,
    AUTH_USER_CREATE,
    AUTH_USER_VIEW,
    AUTH_WORKFLOW_CREATE,
    AUTH_WORKFLOW_VIEW,
} from "domain/authority";
import { OnboardingProductType } from "domain/onboardings";
import { FeatureLicenseType } from "domain/users";
import { onboardingsService } from "services/onboardings/OnboardingsService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { isLicensingModelBundleWithToken, isUserParentInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface LinkDetails {
    featureLicenses: FeatureLicenseType[];
    authorities: string[];
    all: boolean;
    linkTitle: string;
    icon: JSX.Element;
    routePath: string;
    testId: string;
    usageStatisticsAction?: Action;
    toShow: boolean;
    locationState?: { toCreateViews: boolean } | { autoOpenCreateEntitlement: boolean };
}

export const BmpSetup = (props: {
    product: OnboardingProductType;
    showFeedback: boolean;
    step: string;
    showSteps: boolean;
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    type Link = "CUSTOM_VIEWS" | "USERS" | "WORKFLOWS" | "ENTITLEMENTS" | "MCS";
    const history = useHistory();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const linkToVisibility = new Map<Link, LinkDetails>([
        [
            "CUSTOM_VIEWS",
            {
                featureLicenses: ["FEATURE_CUSTOM_REPORT_VIEWS"],
                authorities: [AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN, AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED],
                all: false,
                linkTitle: t("Onboarding.nextSteps.customViews"),
                icon: <Reports color={theme.iconFillColor} />,
                routePath: ALL_REPORTS_ROUTE.path,
                testId: testIds.workArea.onboarding.bmpSetupContainer.reportsLink,
                usageStatisticsAction: Action.CREATE_CUSTOM_VIEW,
                toShow: [OnboardingProductType.BMDE, OnboardingProductType.BDE, OnboardingProductType.BMWE].includes(
                    props.product
                ),
                locationState: { toCreateViews: true },
            },
        ],
        [
            "WORKFLOWS",
            {
                featureLicenses: ["FEATURE_WORKFLOW_BDE", "FEATURE_WORKFLOW_BMDE", "FEATURE_WORKFLOW_LUN"],
                authorities: [AUTH_WORKFLOW_VIEW, AUTH_WORKFLOW_CREATE],
                all: true,
                linkTitle: t("Onboarding.nextSteps.workflows"),
                icon: <Workflows color={theme.iconFillColor} />,
                routePath: ALL_WORKFLOWS_ROUTE.path,
                testId: testIds.workArea.onboarding.bmpSetupContainer.workflowsLink,
                usageStatisticsAction: Action.CREATE_WORKFLOW,
                toShow: [OnboardingProductType.BMDE, OnboardingProductType.BDE, OnboardingProductType.BMWE].includes(
                    props.product
                ),
            },
        ],
        [
            "USERS",
            {
                featureLicenses: [],
                authorities: [AUTH_USER_CREATE, AUTH_USER_VIEW],
                all: true,
                linkTitle: t("Onboarding.nextSteps.users"),
                icon: <Users color={theme.iconFillColor} />,
                routePath: USERS_ALL_USERS_ROUTE.path,
                testId: testIds.workArea.onboarding.bmpSetupContainer.usersLink,
                usageStatisticsAction: Action.INVITE_USER,
                toShow: [OnboardingProductType.BMDE, OnboardingProductType.BDE, OnboardingProductType.BMWE].includes(
                    props.product
                ),
            },
        ],
        [
            "ENTITLEMENTS",
            {
                featureLicenses: [],
                authorities: [AUTH_MC_VIEW, AUTH_EMS_CREATE_ENTITLEMENT],
                all: true,
                linkTitle: t("Onboarding.nextSteps.createEntitlement"),
                icon: <Licenses color={theme.iconFillColor} />,
                routePath: ALL_MCS_ROUTE.path,
                testId: testIds.workArea.onboarding.bmpSetupContainer.entitlementsLink,
                usageStatisticsAction: Action.ADD_ENTITLEMENT,
                toShow: [OnboardingProductType.BMPOP].includes(props.product),
                locationState: { autoOpenCreateEntitlement: true },
            },
        ],
        [
            "MCS",
            {
                featureLicenses: [],
                authorities: [AUTH_MC_VIEW],
                all: true,
                linkTitle: t("Onboarding.nextSteps.mcs"),
                icon: <OnPremBmp color={theme.iconFillColor} />,
                routePath: ALL_MCS_ROUTE.path,
                testId: testIds.workArea.onboarding.bmpSetupContainer.mcsLink,
                toShow: [OnboardingProductType.BMPOP].includes(props.product),
            },
        ],
    ]);
    const [visibleFeedback, setVisibleFeedback] = React.useState(true);

    function createCard(cardProperties: LinkDetails) {
        if (!cardProperties.toShow) {
            return null;
        }
        let featureCheck = cardProperties.featureLicenses.length < 1;
        for (const each of cardProperties.featureLicenses) {
            if (userSessionService.hasFeatureLicense(each)) {
                featureCheck = true;
                break;
            }
        }
        if (
            !featureCheck ||
            !(cardProperties.all
                ? userSessionService.userHasAllAuthorities(cardProperties.authorities)
                : userSessionService.userHasAnyAuthority(cardProperties.authorities))
        ) {
            return null;
        }
        return (
            <div
                className={classNames(style.linkCard, style.linkText)}
                key={cardProperties.linkTitle}
                data-testid={cardProperties.testId}
                data-type={props.product}
                onClick={() => {
                    if (cardProperties.usageStatisticsAction) {
                        usageStatisticsService.sendEvent({
                            category: Category.ONBOARDING,
                            action: cardProperties.usageStatisticsAction,
                            label: props.product,
                        });
                    }
                    if (cardProperties.locationState) {
                        history.push(cardProperties.routePath, cardProperties.locationState);
                    } else {
                        window.open(cardProperties.routePath, "_self");
                    }
                }}
            >
                {cardProperties.icon}
                <div className={style.linkText}>{cardProperties.linkTitle}</div>
            </div>
        );
    }

    const cards = [...linkToVisibility.keys()].map((each) => {
        const link = linkToVisibility.get(each);
        if (link) {
            return link;
        }
    });

    const getSurveyMonkeyId = () => {
        switch (props.product) {
            case OnboardingProductType.BDE:
                return process.env.BDE_SURVEY_MONKEY_ID || "";
            case OnboardingProductType.BMDE:
                return process.env.BMDE_SURVEY_MONKEY_ID || "";
            case OnboardingProductType.BMPOP:
                return process.env.BMPOP_SURVEY_MONKEY_ID || "";
            case OnboardingProductType.BAR:
                return process.env.BAR_SURVEY_MONKEY_ID || "";
            case OnboardingProductType.BMWE:
                return process.env.BMWE_SURVEY_MONKEY_ID || "";
            default:
                return "";
        }
    };

    function createFeedbackForm() {
        if (isUserParentInternal()) {
            return null;
        }

        return (
            <Modal
                isOpen={visibleFeedback}
                hideModal={() => {
                    setVisibleFeedback(false);
                    const abortController: AbortController = new AbortController();
                    abortControllers.push(abortController);
                    onboardingsService.submitFeedback(props.product, props.step, abortController).then(() => {
                        abortControllers.forEach((each) => each.abort());
                    });
                    usageStatisticsService.sendEvent({
                        category: Category.SURVEY,
                        action: Action.CLOSE_CUSTOMER_SURVEY,
                        label: props.product,
                    });
                }}
                width="920px"
            >
                <SurveyMonkeyWidget surveyMonkeyId={getSurveyMonkeyId()} />
            </Modal>
        );
    }

    const showFeedbackForm = props.showFeedback && isLicensingModelBundleWithToken();

    return !props.showSteps ? null : (
        <div className={style.margin}>
            {showFeedbackForm ? createFeedbackForm() : null}
            {props.product !== OnboardingProductType.BAR && (
                <div className={style.title}>{t("Onboarding.nextSteps.whatNext")}</div>
            )}
            <div className={classNames(style.linksContainer, style.threeColumns)}>
                {cards.map((each) => each && createCard(each))}
            </div>
        </div>
    );
};
